import {IDateSelectionStrategy} from "./strategy/date-selection-strategy.interface";
import {makeObservable, observable, reaction, runInAction, IReactionDisposer, computed} from "mobx";
import {DepartureDateSelectionStrategy} from "./strategy/departure-date-selection.strategy";
import {IServiceFactory} from "../../service-factory.interface";
import {MonthModel} from "../../time/month.model";
import {IFlightSearchControllerViewModel} from "../flight-search-controller/flight-search-controller-view-model.interface";
import {ILowFareResult} from "../../low-fare/low-fare-readers/low-fare-reader.interface";
import {NullableString} from "../../../types/nullable-types";
import {ReturnDateSelectionStrategy} from "./strategy/return-date-selection.strategy";

export enum DatesSelectionStrategyEnum {
    DepartureDate,
    ReturnDate
}

export class FlightsDatesSelector {
    constructor(public readonly searchController: IFlightSearchControllerViewModel,
                private readonly services: IServiceFactory) {

        this._currentStrategy = new DepartureDateSelectionStrategy(this.services, searchController);

        makeObservable<this, '_currentStrategy'>(this, {
            _currentStrategy: observable.ref,
            flightsScheduledMonths: computed
        });

        this._reactions.push(reaction(() => this.searchController.isOneWay,
            (isOneWay) => {
                if(isOneWay) {
                    this._setCurrentStrategy(new DepartureDateSelectionStrategy(this.services, this.searchController));
                }

            }));
    }



    private _reactions: IReactionDisposer[] = [];
    private _currentStrategy: IDateSelectionStrategy;

    private _setCurrentStrategy = (strategy: IDateSelectionStrategy) => {
        runInAction(() => {
            this._currentStrategy = strategy;
        });
    }

    get isLoadingSchedule(): boolean {
        return this.searchController.departureFlightSchedule.isLoading || this.searchController.returnFlightSchedule.isLoading;
    }

    get scheduleLoadingError(): NullableString {
        if(this.searchController.isOneWayDepartureTrip) {
            return this.searchController.departureFlightSchedule.errorMessage
        }

        if(this.searchController.isOneWayReturnTrip) {
            return this.searchController.returnFlightSchedule.errorMessage;
        }

        return this.searchController.departureFlightSchedule.errorMessage || this.searchController.returnFlightSchedule.errorMessage;
    }

    getLowFare(date: Date): ILowFareResult {
        return this._currentStrategy.getActiveLowFare(date);
    }

    getDepartureLowFare(date: Date): ILowFareResult {
        return this._currentStrategy.getDepartureLowFare(date);
    }

    getReturnLowFare(date: Date): ILowFareResult {
        return this._currentStrategy.getReturnLowFare(date);
    }

    isDateAvailable(date: Date): boolean {
        return this._currentStrategy.isDateAvailable(date);
    }

    isDateSelected(date: Date): boolean {
        return this.services.time.areDatesEqual(date, this.searchController.departureDate)
               || this.services.time.areDatesEqual(date, this.searchController.returnDate);
    }


    isDateInsideSelection(date: Date): boolean {
        const searchController = this.searchController;
        if(searchController.departureDate && searchController.returnDate) {
            return searchController.departureDate?.getTime() <= date.getTime()
                && date.getTime() <= searchController.returnDate?.getTime();
        } else {
            return false;
        }
    }

    get departureAndReturnDatesAreTheSame(): boolean {
        return this.services.time.areDatesEqual(this.searchController.departureDate, this.searchController.returnDate);
    }

    selectDate(date: Date): void {
        this._setCurrentStrategy(this._currentStrategy.setCurrentDate(date));
    }

    clearDepartureDate(): void {
        this.searchController.returnDate = null;
        this.searchController.departureDate = null;
        this._setCurrentStrategy(new DepartureDateSelectionStrategy(this.services, this.searchController));
    }

    clearReturnDate(): void {
        this.searchController.returnDate = null;
        if(this.searchController.departureDate) {
            this._setCurrentStrategy(new ReturnDateSelectionStrategy(this.services, this.searchController));
        } else {
            this._setCurrentStrategy(new DepartureDateSelectionStrategy(this.services, this.searchController));
        }

    }

    get flightsScheduledMonths(): MonthModel[] {
        const availableDepartureDates = this.searchController.departureFlightSchedule.availableDates;
        const availableReturnDates = this.searchController.returnFlightSchedule.availableDates;

        if(availableDepartureDates.length === 0 && availableReturnDates.length === 0) {
            return [];
        }

        let firstAvailableDate: number;
        let lastAvailableDate: number;

        if(availableReturnDates.length === 0) {
            firstAvailableDate = availableDepartureDates[0].getTime();
            lastAvailableDate = availableDepartureDates[availableDepartureDates.length - 1].getTime();
        } else if(availableDepartureDates.length === 0) {
            firstAvailableDate = availableReturnDates[0].getTime();
            lastAvailableDate = availableReturnDates[availableReturnDates.length - 1].getTime();
        } else {
            firstAvailableDate = Math.min(availableDepartureDates[0].getTime(), availableReturnDates[0].getTime());
            lastAvailableDate = Math.max(availableDepartureDates[availableDepartureDates.length - 1].getTime(),
                                         availableReturnDates[availableReturnDates.length - 1].getTime());
        }


        return this.services.time.getMonthsInRange(new Date(firstAvailableDate), new Date(lastAvailableDate));
    }

    dispose(): void {
        this._reactions.forEach(disposeReaction => disposeReaction());
        this._reactions = [];
    }

    get currentSelectionStrategy(): DatesSelectionStrategyEnum {
        return this._currentStrategy.selectionStrategy;
    }
}




